import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Spinner, Alert, Badge } from 'react-bootstrap';
import Chart from 'react-apexcharts';
import { api } from '../../services/api';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import { FiBriefcase, FiUsers, FiCheckSquare, FiClipboard } from '../../utils/icons';

// ── Paletas por tema (ApexCharts renderiza em SVG e não lê variáveis CSS,
//    então os valores precisam ser resolvidos em JS conforme o tema ativo) ──

interface IPalette {
  surface: string; border: string; text: string; textMuted: string;
  accent: string; track: string; success: string; warning: string; danger: string;
}

const PALETTES: Record<'dark' | 'light', IPalette> = {
  dark: {
    surface: '#0d1220', border: 'rgba(0, 229, 255, 0.16)',
    text: '#e7f1ff', textMuted: '#7c8fb3',
    accent: '#00e5ff', track: '#121a2e',
    success: '#39ff9c', warning: '#ffc94d', danger: '#ff3f6e',
  },
  light: {
    surface: '#ffffff', border: '#c7d7f2',
    text: '#0b1f4d', textMuted: '#4a5d85',
    accent: '#1a56db', track: '#e7f0fc',
    success: '#12946a', warning: '#b8860a', danger: '#d0004a',
  },
} as const;

// ── KPI Card ──────────────────────────────────────────────────────────────────

const KpiCard: React.FC<{ label: string; value: number | string; icon: React.FC<{ size?: number }>; accent: string; p: IPalette }> = ({
  label, value, icon: Icon, accent, p,
}) => (
  <div style={{ background: p.surface, border: `1px solid ${p.border}`, borderRadius: 12, padding: '1rem 1.25rem' }}>
    <div style={{
      width: 36, height: 36, borderRadius: 10, marginBottom: 10,
      background: `${accent}22`, color: accent,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon size={18} />
    </div>
    <div style={{ color: p.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'var(--font-family-mono)' }}>
      {label}
    </div>
    <div style={{ color: p.text, fontSize: '1.85rem', fontWeight: 700, lineHeight: 1.3 }}>
      {value}
    </div>
  </div>
);

// ── Página ─────────────────────────────────────────────────────────────────────

interface IDominio { nome: string; media: number; total: number; }
interface IKpis { empresas: number; questionarios: number; usuarios: number; contratos: number; }

const DashboardPage: React.FC = () => {
  const { empresaId, theme } = useInterfaceStore();
  const p = PALETTES[theme];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dominios, setDominios] = useState<IDominio[]>([]);
  const [mediaGeral, setMediaGeral] = useState(0);
  const [kpis, setKpis] = useState<IKpis | null>(null);

  useEffect(() => {
    if (!empresaId) {
      setError('Selecione uma empresa para visualizar o dashboard.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    api.getDashboardMaturidade(empresaId).then(res => {
      if (!res.success) {
        setError('Erro ao carregar dados do dashboard.');
        return;
      }
      const data = res.data as any;
      const doms: IDominio[] = data.dominios || [];
      setDominios(doms);
      setMediaGeral(data.media_geral ?? 0);
      setKpis(data.kpis ?? null);
    }).catch(() => {
      setError('Erro de conexão com o servidor.');
    }).finally(() => setLoading(false));
  }, [empresaId]);

  // Nomes de domínio costumam ser frases longas — um radar de muitos raios com
  // texto longo fica ilegível (rótulos cortados/sobrepostos). Barra horizontal
  // lida bem com texto longo e ainda mostra o valor exato de cada domínio.
  const dominiosOrdenados = useMemo(
    () => [...dominios].sort((a, b) => a.media - b.media),
    [dominios]
  );

  const barChartOptions: ApexCharts.ApexOptions = useMemo(() => {
    const corPorFaixa = (pct: number) => (pct >= 80 ? p.success : pct >= 60 ? p.accent : pct >= 40 ? p.warning : p.danger);
    return {
    chart: { id: 'maturity-bar', toolbar: { show: false }, foreColor: p.textMuted },
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '55%', distributed: true },
    },
    xaxis: {
      categories: dominiosOrdenados.map(d => d.nome),
      max: 100,
      labels: { formatter: (v) => `${v}%`, style: { colors: p.textMuted } },
    },
    yaxis: {
      labels: {
        style: { colors: dominiosOrdenados.map(() => p.textMuted), fontSize: '11px' },
        maxWidth: 260,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (v: number) => `${v}%`,
      style: { colors: [p.text], fontSize: '11px', fontWeight: 600 },
      offsetX: 4,
    },
    colors: dominiosOrdenados.map(d => corPorFaixa(Math.round(d.media))),
    legend: { show: false },
    grid: { borderColor: p.border, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    tooltip: { y: { formatter: (v: number) => `${v}%` }, theme },
    };
  }, [dominiosOrdenados, p, theme]);

  const gaugeOptions: ApexCharts.ApexOptions = useMemo(() => ({
    chart: { id: 'maturity-gauge', type: 'radialBar', offsetY: -20 },
    plotOptions: {
      radialBar: {
        startAngle: -135,
        endAngle: 135,
        hollow: { size: '70%', background: 'transparent' },
        track: { background: p.track, strokeWidth: '67%', margin: 0 },
        dataLabels: {
          name: { offsetY: -10, show: true, color: p.textMuted, fontSize: '17px' },
          value: {
            formatter: (v: number) => `${Math.round(v)}%`,
            color: p.text,
            fontSize: '36px',
            show: true,
          },
        },
      },
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: theme, type: 'horizontal', shadeIntensity: 0.4,
        gradientToColors: [p.accent], inverseColors: true,
        opacityFrom: 1, opacityTo: 1, stops: [0, 100],
      },
      colors: [p.accent],
    },
    stroke: { lineCap: 'round' },
    labels: ['Maturidade'],
  }), [p, theme]);

  // ── Estados intermediários ────────────────────────────────────────────────

  if (loading) {
    return (
      <Container fluid className="p-4 d-flex justify-content-center align-items-center"
        style={{ height: '60vh' }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-4">
        <h2 className="mb-3">Dashboard de Maturidade</h2>
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  const semDados = dominios.length === 0;

  // ── Nível de maturidade ───────────────────────────────────────────────────
  const nivelLabel = mediaGeral >= 80 ? 'Otimizado'
    : mediaGeral >= 60 ? 'Gerenciado'
    : mediaGeral >= 40 ? 'Definido'
    : mediaGeral >= 20 ? 'Inicial'
    : 'Inexistente';

  const nivelColor = mediaGeral >= 80 ? 'success'
    : mediaGeral >= 60 ? 'primary'
    : mediaGeral >= 40 ? 'warning'
    : 'danger';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Container fluid className="p-4">
      {/* Cabeçalho */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <div>
          <h2 className="mb-0">Dashboard de Maturidade</h2>
          <p className="text-muted mb-0">Visão geral da conformidade LGPD da organização.</p>
        </div>
        {!semDados && (
          <Badge bg={nivelColor} className="fs-6 ms-auto">
            Nível: {nivelLabel}
          </Badge>
        )}
      </div>

      {/* KPI Cards */}
      {kpis && (
        <Row className="g-3 mb-4">
          <Col xs={6} md={3}>
            <KpiCard label="Empresas" value={kpis.empresas} icon={FiBriefcase} accent={p.accent} p={p} />
          </Col>
          <Col xs={6} md={3}>
            <KpiCard label="Usuários" value={kpis.usuarios} icon={FiUsers} accent={p.success} p={p} />
          </Col>
          <Col xs={6} md={3}>
            <KpiCard label="Questionários" value={kpis.questionarios} icon={FiCheckSquare} accent={p.warning} p={p} />
          </Col>
          <Col xs={6} md={3}>
            <KpiCard label="Fornecedores" value={kpis.contratos} icon={FiClipboard} accent={p.danger} p={p} />
          </Col>
        </Row>
      )}

      {/* Sem dados */}
      {semDados ? (
        <Alert variant="info">
          <Alert.Heading>Sem dados de maturidade</Alert.Heading>
          <p>
            Ainda não há respostas de questionários registradas. Responda os questionários
            de conformidade LGPD para que os indicadores de maturidade apareçam aqui.
          </p>
        </Alert>
      ) : (
        <>
          {/* Gráficos */}
          <Row className="g-4 mb-4">
            <Col xs={12} md={7}>
              <div style={{ background: p.surface, border: `1px solid ${p.border}`, borderRadius: 16, padding: '1.25rem', height: '100%' }}>
                <h5 style={{ color: p.text, marginBottom: 4 }}>Maturidade por Domínio</h5>
                <p style={{ color: p.textMuted, fontSize: '0.85rem', marginBottom: 0 }}>
                  Domínios ordenados do mais crítico ao mais maduro
                </p>
                <Chart
                  options={barChartOptions}
                  series={[{ name: 'Maturidade', data: dominiosOrdenados.map(d => Math.round(d.media)) }]}
                  type="bar"
                  height={Math.max(260, dominiosOrdenados.length * 34)}
                />
              </div>
            </Col>
            <Col xs={12} md={5}>
              <div style={{ background: p.surface, border: `1px solid ${p.border}`, borderRadius: 16, padding: '1.25rem', height: '100%' }}>
                <h5 style={{ color: p.text, marginBottom: 4 }}>Score Geral</h5>
                <p style={{ color: p.textMuted, fontSize: '0.85rem', marginBottom: 0 }}>
                  Média consolidada de todos os domínios
                </p>
                <Chart
                  options={gaugeOptions}
                  series={[mediaGeral]}
                  type="radialBar"
                  height={300}
                />
                <div className="d-flex justify-content-center" style={{ marginTop: -12 }}>
                  <Badge style={{ background: p.track, color: p.textMuted, border: `1px solid ${p.border}` }}>
                    {dominios.filter(d => d.media >= 60).length} de {dominios.length} domínios acima de 60%
                  </Badge>
                </div>
              </div>
            </Col>
          </Row>

          {/* Tabela de domínios */}
          <div style={{ background: p.surface, border: `1px solid ${p.border}`, borderRadius: 16, padding: '1.25rem' }}>
            <h5 style={{ color: p.text, marginBottom: '1rem' }}>Detalhe por Domínio</h5>
            <table className="table table-hover mb-0" style={{ fontSize: '0.9rem' }}>
              <thead>
                <tr>
                  <th>Domínio</th>
                  <th className="text-center">Respostas</th>
                  <th>Maturidade</th>
                  <th className="text-end">Score</th>
                </tr>
              </thead>
              <tbody>
                {dominios.map(d => {
                  const pct = Math.round(d.media);
                  const cor = pct >= 80 ? p.success : pct >= 60 ? p.accent : pct >= 40 ? p.warning : p.danger;
                  return (
                    <tr key={d.nome}>
                      <td>{d.nome}</td>
                      <td className="text-center">{d.total}</td>
                      <td style={{ minWidth: 140 }}>
                        <div style={{ height: 6, borderRadius: 3, background: p.track, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: cor, borderRadius: 3 }} />
                        </div>
                      </td>
                      <td className="text-end">
                        <span style={{ color: cor, fontWeight: 600 }}>{pct}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Container>
  );
};

export default DashboardPage;
